import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  Typography,
  Alert,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  TextField,
  Autocomplete,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListSubheader,
  FormControlLabel,
  Checkbox,
  Grid,
  Snackbar,
  Stepper,
  Step,
  StepLabel,
  InputAdornment,
  Tabs,
  Tab,
  Link as MuiLink,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams, GridRowSelectionModel } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Link as LinkIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  OpenInNew as OpenInNewIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { adminApi, Notification, NotificationType, NotificationStatus, NotificationCta, NotificationCtaType, Organization, User } from '../services/api';
import {
  NotificationToolRoute,
  isCustomToolRoute,
  isSafeRelativePath,
  visibleToolRoutes,
} from '../constants/notificationToolRoutes';

import NotificationMediaUpload from '../components/NotificationMediaUpload';
import NotificationEmailDialog from '../components/NotificationEmailDialog';

/**
 * Whether an announcement can be EMAILED to its targeted organisations
 * (Trello #395). Mirrors the backend's own guards so the button is absent
 * rather than armed-then-rejected:
 *
 *  · active only  — a pending row is an unapproved draft and an inactive one
 *                   has been withdrawn; neither should reach an inbox.
 *  · not user-targeted — announcement email goes to everyone in the targeted
 *                   organisations, so on a notification scoped to named users
 *                   it would reach people the admin deliberately excluded.
 */
export const canEmailNotification = (notification: Notification): boolean =>
  notification.status === 'active' && !(notification.userIds && notification.userIds.length > 0);

/** Sentinel for the "type a path yourself" entry in the tool picker. */
const CUSTOM_ROUTE_OPTION = '__custom__';

const IS_BROKER_ADMIN = import.meta.env.VITE_IS_BROKER_ADMIN === 'true';

/**
 * Routes offered by THIS build, grouped for the picker. A broker admin never
 * sees the Intelligent Automation tools — those routes don't exist for their
 * clients, so a CTA pointing at one 404s for every recipient, which is exactly
 * the failure the picker exists to prevent.
 */
export const TOOL_ROUTE_GROUPS: Array<[string, NotificationToolRoute[]]> = Object.entries(
  visibleToolRoutes(IS_BROKER_ADMIN).reduce<Record<string, NotificationToolRoute[]>>((acc, route) => {
    (acc[route.group] ||= []).push(route);
    return acc;
  }, {}),
);

/**
 * True while the picker should sit on "Custom path…" — either the sentinel is
 * stored (the admin just chose it and hasn't typed a path yet) or the value is
 * a real off-list path (editing an existing notification).
 */
export const isCustomPath = (url?: string): boolean =>
  url === CUSTOM_ROUTE_OPTION || isCustomToolRoute(url);

type TabKey = 'all' | 'pending' | 'active' | 'inactive';

const DEFAULT_EXPIRY = () => new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 16);

/**
 * Merge the currently-selected organization IDs with another batch of IDs
 * (e.g. the visible filtered set when "Select all filtered" is clicked).
 * Selection must be ADDITIVE across searches — replacing the selection with
 * only the visible rows drops organizations picked under a previous search
 * query, which is exactly the "can only select one org at a time" bug
 * (Trello #215).
 */
export const mergeOrgSelections = (current: string[], additions: string[]): string[] =>
  Array.from(new Set([...current, ...additions]));

/**
 * Clean the CTA builder rows before submit (Trello #246 #7): drop rows the admin
 * started but left without a label, trim label/url, and omit an empty url so the
 * backend stores `{ type, label }` rather than `{ type, label, url: '' }`.
 */
export const cleanNotificationCtas = (ctas: NotificationCta[]): NotificationCta[] =>
  ctas
    .filter((c) => c.label.trim())
    .map((c) => ({
      type: c.type,
      label: c.label.trim(),
      ...(c.url && c.url.trim() ? { url: c.url.trim() } : {}),
    }));

/**
 * Parse the comma-separated per-user targeting input into a deduped list of
 * trimmed, non-empty entries. Each entry is an email OR a Cognito sub id — the
 * backend resolves emails to subs (Trello #267).
 */
export const parseUserIds = (raw: string): string[] =>
  Array.from(new Set(raw.split(',').map((s) => s.trim()).filter(Boolean)));

/**
 * A selected per-user notification target (Trello #279). `value` is what gets
 * sent to the backend — a Cognito sub when the user was picked from search, or a
 * raw email when typed free-form (the backend resolves emails to subs). `label`
 * is the chip / option display text.
 */
export interface TargetUser {
  value: string;
  label: string;
}

/** Build a picker option from a user-search result (email — Name, falling back to the id). */
export const userToTargetOption = (u: User): TargetUser => {
  const name = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
  const label = u.email ? (name ? `${u.email} — ${name}` : u.email) : u.userId;
  return { value: u.userId, label };
};

/**
 * Normalise an Autocomplete entry (a picked TargetUser object OR a free-typed
 * string) into TargetUser(s). A typed string is run through parseUserIds so a
 * pasted comma-list becomes several chips; each becomes an email-or-sub target.
 */
export const normalizeTargetEntries = (entry: TargetUser | string): TargetUser[] => {
  if (typeof entry !== 'string') return [entry];
  return parseUserIds(entry).map((v) => ({ value: v, label: v }));
};

/** First-wins dedupe of selected targets by `value` (drops blanks). */
export const dedupeTargetUsers = (targets: TargetUser[]): TargetUser[] => {
  const seen = new Set<string>();
  const out: TargetUser[] = [];
  for (const t of targets) {
    if (t.value && !seen.has(t.value)) {
      seen.add(t.value);
      out.push(t);
    }
  }
  return out;
};

/** Map the selected targets to the deduped `userIds` array sent on submit. */
export const targetUsersToUserIds = (targets: TargetUser[]): string[] =>
  dedupeTargetUsers(targets).map((t) => t.value);

/**
 * A notification needs an audience. Three ways to define one (Trello #267):
 *   1. send-to-all-organizations,
 *   2. ≥1 specific organization, OR
 *   3. ≥1 specific user.
 * Targeting specific users is a COMPLETE audience on its own — the backend
 * scopes delivery to those subs (notificationRepository AND-combines `userIds`
 * with the org filter, and an empty `organizationIds` means "everyone", so the
 * user list fully narrows it). Returns true when NONE of the three is set, i.e.
 * the form has no audience yet and submit must be blocked. The earlier bug
 * required an org even when specific users were targeted, blocking the exact
 * per-user flow this feature exists for.
 */
export const notificationAudienceMissing = (opts: {
  sendToAllOrganizations: boolean;
  organizationIds: string[];
  userIds: string[];
}): boolean =>
  !opts.sendToAllOrganizations &&
  opts.organizationIds.length === 0 &&
  opts.userIds.length === 0;

/**
 * CTA types that need a destination — the broker renderer
 * (legal-opinion-frontend NotificationCtas.resolveCta) silently drops one of
 * these with no url, so the admin must supply one (Trello #267). upgrade /
 * buy-tokens resolve their own in-app destination and need no url.
 *
 * `open-tool` needs a url too, but a RELATIVE one — see findCtaWithInvalidUrl.
 */
export const ctaNeedsUrl = (type: NotificationCtaType): boolean =>
  type !== 'upgrade' && type !== 'buy-tokens';

/** http(s)-only — a CTA url must be a real web link, never javascript:/data:/etc. */
const isHttpUrl = (url: string): boolean => {
  try {
    const u = new URL(url);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
};

/**
 * Return the first CTA that needs a url but is missing it OR carries the wrong
 * KIND of url (Trello #267) — used to block submit of a button that would
 * otherwise save and then silently vanish, and to keep the client guard honest
 * against a javascript:/data: scheme (the backend ctaSchema rejects those too).
 * Run on the CLEANED ctas (cleanNotificationCtas strips an empty url, so `!c.url`
 * catches an absent or blank url).
 *
 * `open-tool` is validated against the RELATIVE-path shape rather than http(s):
 * it navigates in-app, and an absolute URL there would be an off-site redirect
 * wearing an in-app button. Checking it with the same rule as the external types
 * would reject every valid value.
 */
export const findCtaWithInvalidUrl = (ctas: NotificationCta[]): NotificationCta | undefined =>
  ctas.find((c) => {
    if (!ctaNeedsUrl(c.type)) return false;
    if (c.type === 'open-tool') return !isSafeRelativePath(c.url);
    return !c.url || !isHttpUrl(c.url);
  });


const NotificationsPage: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [organizationsLoading, setOrganizationsLoading] = useState(true);
  const [organizationsError, setOrganizationsError] = useState<string | null>(null);
  // Tab selection — defaults to All so existing flows (review the full list)
  // stay unchanged. The orange `N pending review` chip in the header pulls the
  // admin to the Pending tab when there's something to act on.
  const [tab, setTab] = useState<TabKey>('all');

  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [detailNotification, setDetailNotification] = useState<Notification | null>(null);
  // Trello #395 — the notification whose announcement email is being drafted.
  const [emailNotification, setEmailNotification] = useState<Notification | null>(null);
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [organizationSearch, setOrganizationSearch] = useState('');

  // Per-user target search (Trello #279) — search the pool by email/name and pick
  // users, instead of hand-typing Cognito ids. Reuses the same list-users endpoint
  // the Users page uses (server-side "contains" search).
  const [userOptions, setUserOptions] = useState<TargetUser[]>([]);
  const [userSearchInput, setUserSearchInput] = useState('');
  const [userSearchLoading, setUserSearchLoading] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    type: 'info' as NotificationType,
    link: '',
    linkText: '',
    // Feature-demo media — upload-produced paths, set by the upload control.
    videoUrl: '',
    imageUrl: '',
    isModal: false,
    expiresAt: DEFAULT_EXPIRY(),
    status: 'active' as NotificationStatus,
    // #267: default OFF — the audience-required validation (notificationAudienceMissing) blocks a no-audience submit.
    sendToAllOrganizations: false,
    organizationIds: [] as string[],
    // Trello #246 #7: multi-CTA buttons + per-user targeting.
    ctas: [] as NotificationCta[],
    // Trello #279: selected target users (picked subs or typed emails); sent as userIds on submit.
    targetUsers: [] as TargetUser[],
  });

  // Debounced user search for the per-user target picker (Trello #279). Hits the
  // same list-users endpoint as the Users page; a guard ignores stale responses.
  useEffect(() => {
    const term = userSearchInput.trim();
    if (term.length < 2) {
      setUserOptions([]);
      setUserSearchLoading(false);
      return;
    }
    setUserSearchLoading(true);
    let cancelled = false;
    const id = setTimeout(async () => {
      try {
        const response = await adminApi.listUsers({ search: term, limit: 10, includeUsage: false });
        if (cancelled) return;
        const users: User[] = response?.data?.users || [];
        setUserOptions(users.map(userToTargetOption));
      } catch {
        if (!cancelled) setUserOptions([]);
      } finally {
        if (!cancelled) setUserSearchLoading(false);
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(id);
    };
  }, [userSearchInput]);

  // Snackbar state
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    fetchNotifications();
    fetchOrganizations();
  }, []);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await adminApi.listNotifications();
      if (response.success) {
        setNotifications(response.data.notifications);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizations = async () => {
    try {
      setOrganizationsLoading(true);
      setOrganizationsError(null);
      const response = await adminApi.listOrganizations();
      if (response.success) {
        setOrganizations(response.data.organizations || []);
      }
    } catch (err: any) {
      setOrganizationsError(err.response?.data?.message || err.message || 'Failed to load organizations');
    } finally {
      setOrganizationsLoading(false);
    }
  };

  const resetDialogState = () => {
    setActiveStep(0);
    setOrganizationSearch('');
    setFormError(null);
  };

  const handleCloseDialog = () => {
    if (formLoading) return;
    resetDialogState();
    setDialogOpen(false);
  };

  const handleOpenCreate = () => {
    setSelectedNotification(null);
    setFormData({
      title: '',
      message: '',
      type: 'info',
      link: '',
      linkText: '',
      videoUrl: '',
      imageUrl: '',
      isModal: false,
      expiresAt: DEFAULT_EXPIRY(),
      status: 'active',
      sendToAllOrganizations: false, // Trello #267: default OFF (see create-default above)
      organizationIds: [],
      ctas: [],
      targetUsers: [],
    });
    resetDialogState();
    setDialogOpen(true);
  };

  const handleOpenEdit = (notification: Notification) => {
    const notificationOrganizations = notification.organizationIds || [];
    setSelectedNotification(notification);
    setFormData({
      title: notification.title || '',
      message: notification.message,
      type: notification.type,
      link: notification.link || '',
      linkText: notification.linkText || '',
      videoUrl: notification.videoUrl || '',
      imageUrl: notification.imageUrl || '',
      isModal: notification.isModal,
      expiresAt: new Date(notification.expiresAt).toISOString().slice(0, 16),
      status: notification.status,
      sendToAllOrganizations: notificationOrganizations.length === 0,
      organizationIds: notificationOrganizations,
      ctas: notification.ctas || [],
      // Existing notifications store resolved subs; show each as its own chip.
      targetUsers: (notification.userIds || []).map((id) => ({ value: id, label: id })),
    });
    resetDialogState();
    setDialogOpen(true);
  };

  const handleOpenDelete = (notification: Notification) => {
    setSelectedNotification(notification);
    setDeleteDialogOpen(true);
  };

  const handleNextStep = () => {
    if (!formData.message || !formData.expiresAt) {
      setFormError('Message and Expiration Date are required');
      return;
    }
    setFormError(null);
    setActiveStep(1);
  };

  const handleBackStep = () => {
    setFormError(null);
    setActiveStep(0);
  };

  // Trello #246 #7 — CTA builder (max 3 buttons per notification).
  const MAX_CTAS = 3;
  const addCta = () => {
    if (formData.ctas.length >= MAX_CTAS) return;
    setFormData({ ...formData, ctas: [...formData.ctas, { type: 'upgrade', label: '', url: '' }] });
  };
  const updateCta = (index: number, patch: Partial<NotificationCta>) => {
    setFormData({
      ...formData,
      ctas: formData.ctas.map((c, i) => (i === index ? { ...c, ...patch } : c)),
    });
  };
  const removeCta = (index: number) => {
    setFormData({ ...formData, ctas: formData.ctas.filter((_, i) => i !== index) });
  };
  // The in-app CTAs route internally (no URL needed); the rest open an external URL.
  const handleFormSubmit = async () => {
    if (!formData.message || !formData.expiresAt) {
      setFormError('Message and Expiration Date are required');
      return;
    }

    if (
      notificationAudienceMissing({
        sendToAllOrganizations: formData.sendToAllOrganizations,
        organizationIds: formData.organizationIds,
        userIds: targetUsersToUserIds(formData.targetUsers),
      })
    ) {
      setFormError(
        'Select at least one organization, target specific users, or enable "Send to all organizations"',
      );
      return;
    }

    try {
      setFormLoading(true);
      setFormError(null);

      // Convert datetime-local to ISO string
      const expiresAtISO = new Date(formData.expiresAt).toISOString();
      const normalizedOrganizationIds = formData.sendToAllOrganizations
        ? []
        : Array.from(new Set(formData.organizationIds));

      // Trello #246 #7: drop CTAs with no label. Trello #279: map the picked /
      // typed target users to their userIds (subs or emails the backend resolves).
      const cleanedCtas = cleanNotificationCtas(formData.ctas);
      const parsedUserIds = targetUsersToUserIds(formData.targetUsers);

      // Trello #267: a link/support/feedback/book-demo CTA with no (or a
      // non-http(s)) URL has no usable destination and is silently dropped by the
      // broker renderer (resolveCta) — block submit with a clear error rather than
      // let it save-and-vanish (the backend rejects it too, as defense-in-depth).
      const ctaInvalidUrl = findCtaWithInvalidUrl(cleanedCtas);
      if (ctaInvalidUrl) {
        setFormError(
          ctaInvalidUrl.type === 'open-tool'
            ? `The "${ctaInvalidUrl.label}" button needs an in-app tool path like /titan-personal-roa — pick one from the list, or type a path starting with "/".`
            : `The "${ctaInvalidUrl.label}" button needs a valid http(s) URL — only Upgrade / Buy runs route in-app without one.`,
        );
        return;
      }

      if (selectedNotification) {
        await adminApi.updateNotification(selectedNotification.id, {
          title: formData.title,
          message: formData.message,
          type: formData.type,
          link: formData.link,
          linkText: formData.linkText,
          ctas: cleanedCtas,
          videoUrl: formData.videoUrl,
          imageUrl: formData.imageUrl,
          isModal: formData.isModal,
          expiresAt: expiresAtISO,
          status: formData.status,
          organizationIds: normalizedOrganizationIds,
          userIds: parsedUserIds,
        });
        showSnackbar('Notification updated successfully', 'success');
      } else {
        await adminApi.createNotification({
          title: formData.title,
          message: formData.message,
          type: formData.type,
          link: formData.link,
          linkText: formData.linkText,
          ctas: cleanedCtas,
          videoUrl: formData.videoUrl,
          imageUrl: formData.imageUrl,
          isModal: formData.isModal,
          expiresAt: expiresAtISO,
          organizationIds: normalizedOrganizationIds,
          userIds: parsedUserIds,
        });
        showSnackbar('Notification created successfully', 'success');
      }

      setDialogOpen(false);
      resetDialogState();
      fetchNotifications();
    } catch (err: any) {
      setFormError(err.response?.data?.message || err.message || 'Action failed');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedNotification) return;

    try {
      setFormLoading(true);
      await adminApi.deleteNotification(selectedNotification.id);
      showSnackbar('Notification deleted successfully', 'success');
      setDeleteDialogOpen(false);
      fetchNotifications();
    } catch (err: any) {
      showSnackbar(err.response?.data?.message || err.message || 'Delete failed', 'error');
    } finally {
      setFormLoading(false);
    }
  };

  // Flip a CI-published draft from 'pending' to 'active' so brokers see it.
  const handlePublish = async (notification: Notification) => {
    try {
      await adminApi.publishNotification(notification.id);
      showSnackbar('Notification published — broadcasting to brokers now.', 'success');
      fetchNotifications();
    } catch (err: any) {
      showSnackbar(err.response?.data?.message || err.message || 'Publish failed', 'error');
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const getTypeColor = (type: NotificationType) => {
    switch (type) {
      case 'info': return 'primary';
      case 'success': return 'success';
      case 'warning': return 'warning';
      case 'feature': return 'secondary';
      default: return 'default';
    }
  };

  const isOrgSpecific = (notification: Notification): boolean =>
    !!(notification.organizationIds && notification.organizationIds.length > 0);

  const filteredOrganizations = useMemo(() => {
    const query = organizationSearch.trim().toLowerCase();
    if (!query) return organizations;

    return organizations.filter((org) => {
      const name = (org.name || '').toLowerCase();
      const displayName = (org.displayName || '').toLowerCase();
      const domain = (org.domain || '').toLowerCase();
      return name.includes(query) || displayName.includes(query) || domain.includes(query);
    });
  }, [organizations, organizationSearch]);

  const organizationSelectionModel: GridRowSelectionModel = formData.organizationIds;

  const columns: GridColDef[] = [
    {
      field: 'type',
      headerName: 'Type',
      width: 110,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Chip
            label={params.value}
            size="small"
            color={getTypeColor(params.value as NotificationType)}
            variant="outlined"
          />
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 110,
      renderCell: (params: GridRenderCellParams) => {
        const status = params.value as NotificationStatus;
        const color = status === 'active' ? 'success' : status === 'pending' ? 'warning' : 'default';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Chip label={status} size="small" color={color} />
          </Box>
        );
      },
    },
    {
      field: 'source',
      headerName: 'Source',
      width: 130,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const src = (params.row as Notification).source;
        if (!src || (!src.mrUrl && !src.trelloUrl)) {
          return <Box sx={{ height: '100%' }} />;
        }
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, height: '100%' }}>
            {src.mrUrl && (
              <Tooltip title={`MR !${src.mrIid ?? ''}`}>
                <MuiLink href={src.mrUrl} target="_blank" rel="noopener" onClick={(e) => e.stopPropagation()}>
                  <Chip
                    size="small"
                    icon={<OpenInNewIcon fontSize="small" />}
                    label={src.mrIid ? `MR !${src.mrIid}` : 'MR'}
                    variant="outlined"
                    sx={{ cursor: 'pointer' }}
                  />
                </MuiLink>
              </Tooltip>
            )}
            {src.trelloUrl && (
              <Tooltip title="Trello card">
                <MuiLink href={src.trelloUrl} target="_blank" rel="noopener" onClick={(e) => e.stopPropagation()}>
                  <Chip
                    size="small"
                    icon={<OpenInNewIcon fontSize="small" />}
                    label="Trello"
                    variant="outlined"
                    color="info"
                    sx={{ cursor: 'pointer' }}
                  />
                </MuiLink>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: 'organizations',
      headerName: 'Organizations',
      width: 150,
      sortable: false,
      valueGetter: (_value, row) => isOrgSpecific(row as Notification) ? 'Org specific' : 'All orgs',
      renderCell: (params: GridRenderCellParams) => {
        const specific = isOrgSpecific(params.row as Notification);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Chip
              size="small"
              label={specific ? 'Org specific' : 'All orgs'}
              color={specific ? 'primary' : 'default'}
              variant="outlined"
              onClick={specific ? (e) => { e.stopPropagation(); setDetailNotification(params.row as Notification); } : undefined}
              sx={specific ? { cursor: 'pointer' } : undefined}
            />
          </Box>
        );
      },
    },
    {
      field: 'title',
      headerName: 'Title',
      flex: 1,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {params.value || '-'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'message',
      headerName: 'Message',
      flex: 2,
      minWidth: 250,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Tooltip title={params.value}>
            <Typography variant="body2" noWrap sx={{ maxWidth: '100%' }}>
              {params.value}
            </Typography>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: 'expiresAt',
      headerName: 'Expires',
      width: 180,
      // type: 'date' makes DataGrid sort + filter chronologically. The
      // valueGetter MUST return a Date — a formatted string sorted lexically.
      // The renderCell formats that Date itself (not params.formattedValue), so
      // a Date object can never reach React as a child.
      type: 'date',
      valueGetter: (value: string | null) => (value ? new Date(value) : null),
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2">
            {params.value instanceof Date ? params.value.toLocaleString() : ''}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 170,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as Notification;
        return (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', height: '100%' }}>
            {row.status === 'pending' && (
              <Tooltip title="Publish — broadcast this to brokers now">
                <IconButton size="small" color="success" onClick={() => handlePublish(row)}>
                  <CheckCircleIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {canEmailNotification(row) && (
              <Tooltip
                title={
                  row.announcementEmailSentAt
                    ? `Emailed on ${new Date(row.announcementEmailSentAt).toLocaleString()}`
                    : 'Email this announcement to the targeted organisations'
                }
              >
                <IconButton
                  size="small"
                  color={row.announcementEmailSentAt ? 'default' : 'info'}
                  onClick={() => setEmailNotification(row)}
                  data-testid="email-notification-button"
                >
                  <EmailIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Edit">
              <IconButton size="small" color="primary" onClick={() => handleOpenEdit(row)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={row.status === 'pending' ? 'Discard pending draft' : 'Delete'}>
              <IconButton size="small" color="error" onClick={() => handleOpenDelete(row)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  const organizationColumns: GridColDef[] = [
    { field: 'name', headerName: 'Organization ID', flex: 1, minWidth: 180 },
    { field: 'displayName', headerName: 'Display Name', flex: 1, minWidth: 180, valueGetter: (value) => value || '-' },
    { field: 'domain', headerName: 'Domain', flex: 1, minWidth: 180, valueGetter: (value) => value || '-' },
  ];

  if (loading && notifications.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const pendingCount = notifications.filter((n) => n.status === 'pending').length;
  const activeCount = notifications.filter((n) => n.status === 'active').length;
  const inactiveCount = notifications.filter((n) => n.status === 'inactive').length;
  const visibleRows =
    tab === 'all'
      ? notifications
      : notifications.filter((n) => n.status === tab);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h4" fontWeight={700}>
            Notifications
          </Typography>
          <Chip label={`${notifications.length} total`} color="primary" variant="outlined" />
          {pendingCount > 0 && (
            <Chip
              label={`${pendingCount} pending review`}
              color="warning"
              onClick={() => setTab('pending')}
              sx={{ cursor: 'pointer' }}
            />
          )}
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreate}
        >
          Create Notification
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <Tabs
          value={tab}
          onChange={(_e, v: TabKey) => setTab(v)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab
            value="pending"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Pending review
                {pendingCount > 0 && <Chip size="small" color="warning" label={pendingCount} />}
              </Box>
            }
          />
          <Tab
            value="active"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Active
                <Chip size="small" color="success" variant="outlined" label={activeCount} />
              </Box>
            }
          />
          <Tab
            value="inactive"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Inactive
                <Chip size="small" variant="outlined" label={inactiveCount} />
              </Box>
            }
          />
          <Tab value="all" label="All" />
        </Tabs>
        <DataGrid
          rows={visibleRows}
          columns={columns}
          autoHeight
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
            sorting: {
              sortModel: [{ field: 'createdAt', sort: 'desc' }],
            },
          }}
          sx={{
            border: 'none',
            '& .MuiDataGrid-cell:focus': { outline: 'none' },
          }}
        />
      </Card>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} fullScreen>
        <DialogTitle>
          <Box sx={{ maxWidth: 1100, mx: 'auto', width: '100%' }}>
            {selectedNotification ? 'Edit Notification' : 'Create New Notification'}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ maxWidth: 1100, mx: 'auto', width: '100%' }}>
            <Box sx={{ mb: 3 }}>
              <Stepper activeStep={activeStep}>
                <Step>
                  <StepLabel>Notification Details</StepLabel>
                </Step>
                <Step>
                  <StepLabel>Organizations</StepLabel>
                </Step>
              </Stepper>
            </Box>
          </Box>

          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}

          {activeStep === 0 && (
            <Box sx={{ maxWidth: 1100, mx: 'auto', mt: 0.5 }}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 7 }}>
                  <Box sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, height: '100%' }}>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                      Content
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12 }}>
                        <TextField
                          fullWidth
                          label="Title (Optional)"
                          placeholder="e.g. New Feature Alert!"
                          value={formData.title}
                          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        />
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <TextField
                          fullWidth
                          required
                          multiline
                          rows={6}
                          label="Message"
                          placeholder="The content of the notification shown to users..."
                          value={formData.message}
                          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, md: 5 }}>
                  <Box sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                      Display Options
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12 }}>
                        <FormControl fullWidth required>
                          <InputLabel>Type</InputLabel>
                          <Select
                            value={formData.type}
                            label="Type"
                            onChange={(e) => setFormData({ ...formData, type: e.target.value as NotificationType })}
                          >
                            <MenuItem value="info">Info (Blue)</MenuItem>
                            <MenuItem value="success">Success (Green)</MenuItem>
                            <MenuItem value="warning">Warning (Orange)</MenuItem>
                            <MenuItem value="feature">Feature (Purple)</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <TextField
                          fullWidth
                          required
                          label="Expires At"
                          type="datetime-local"
                          value={formData.expiresAt}
                          onChange={(e) => setFormData({ ...formData, expiresAt: e.target.value })}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <TextField
                          fullWidth
                          label="Action Link (Optional)"
                          placeholder="https://..."
                          value={formData.link}
                          onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                          InputProps={{
                            startAdornment: <LinkIcon fontSize="small" color="action" sx={{ mr: 1 }} />,
                          }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <TextField
                          fullWidth
                          label="Link Button Text"
                          placeholder="Learn More"
                          value={formData.linkText}
                          onChange={(e) => setFormData({ ...formData, linkText: e.target.value })}
                        />
                      </Grid>

                      {/* CTA buttons (Trello #246 #7) — supersede the single link above */}
                      <Grid size={{ xs: 12 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            Call-to-action buttons (optional)
                          </Typography>
                          <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={addCta}
                            disabled={formData.ctas.length >= MAX_CTAS}
                          >
                            Add button
                          </Button>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                          Up to {MAX_CTAS}. &quot;Open a tool&quot; takes the client straight into the feature in-app — the best way to get a new feature actually used. &quot;Upgrade&quot; / &quot;Buy runs&quot; route in-app too; the rest open the URL you provide in a new tab. When set, these replace the single link above.
                        </Typography>
                        {formData.ctas.map((cta, index) => (
                          <Box
                            key={index}
                            sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1.5, flexWrap: 'wrap' }}
                            data-testid={`cta-row-${index}`}
                          >
                            <FormControl sx={{ minWidth: 150 }} size="small">
                              <InputLabel>Type</InputLabel>
                              <Select
                                label="Type"
                                value={cta.type}
                                onChange={(e) => updateCta(index, { type: e.target.value as NotificationCtaType })}
                                inputProps={{ 'data-testid': `cta-type-${index}` }}
                              >
                                <MenuItem value="open-tool">Open a tool (in-app)</MenuItem>
                                <MenuItem value="upgrade">Upgrade</MenuItem>
                                <MenuItem value="buy-tokens">Buy runs</MenuItem>
                                <MenuItem value="feedback">Feedback</MenuItem>
                                <MenuItem value="support">Support</MenuItem>
                                <MenuItem value="book-demo">Book a demo</MenuItem>
                                <MenuItem value="link">Link</MenuItem>
                              </Select>
                            </FormControl>
                            <TextField
                              size="small"
                              label="Button text"
                              value={cta.label}
                              onChange={(e) => updateCta(index, { label: e.target.value })}
                              sx={{ flex: '1 1 140px' }}
                              inputProps={{ 'data-testid': `cta-label-${index}` }}
                            />
                            {cta.type === 'open-tool' ? (
                              // A picker, not a text field: a typo'd path renders a
                              // button that navigates to a 404, and nothing else in
                              // the authoring flow would catch it.
                              <FormControl sx={{ flex: '1 1 220px' }} size="small">
                                <InputLabel>Tool to open</InputLabel>
                                <Select
                                  label="Tool to open"
                                  // The sentinel is the STORED value while the
                                  // admin is typing a custom path. Clearing it to
                                  // '' instead would make isCustomToolRoute('')
                                  // false on the next render, deselecting this
                                  // option and unmounting the very field it opens
                                  // — so the escape hatch could never be used.
                                  // isSafeRelativePath rejects the sentinel, so
                                  // submit stays blocked until a real path is typed.
                                  value={isCustomPath(cta.url) ? CUSTOM_ROUTE_OPTION : (cta.url || '')}
                                  onChange={(e) => updateCta(index, { url: e.target.value })}
                                  inputProps={{ 'data-testid': `cta-tool-${index}` }}
                                >
                                  {TOOL_ROUTE_GROUPS.map(([group, routes]) => [
                                    <ListSubheader key={`group-${group}`}>{group}</ListSubheader>,
                                    ...routes.map((route) => (
                                      <MenuItem key={route.path} value={route.path}>
                                        {route.label}
                                      </MenuItem>
                                    )),
                                  ])}
                                  <MenuItem value={CUSTOM_ROUTE_OPTION}>Custom path…</MenuItem>
                                </Select>
                              </FormControl>
                            ) : (
                              <TextField
                                size="small"
                                label={ctaNeedsUrl(cta.type) ? 'URL (required)' : 'URL (optional)'}
                                placeholder="https://..."
                                value={cta.url || ''}
                                onChange={(e) => updateCta(index, { url: e.target.value })}
                                sx={{ flex: '1 1 200px' }}
                                inputProps={{ 'data-testid': `cta-url-${index}` }}
                              />
                            )}
                            {cta.type === 'open-tool' && isCustomPath(cta.url) && (
                              <TextField
                                size="small"
                                label="Custom in-app path"
                                placeholder="/titan-personal-roa"
                                // Blank while the sentinel is stored, so the field
                                // starts empty rather than showing '__custom__'.
                                value={cta.url === CUSTOM_ROUTE_OPTION ? '' : cta.url || ''}
                                onChange={(e) => updateCta(index, { url: e.target.value || CUSTOM_ROUTE_OPTION })}
                                error={!!cta.url && !isSafeRelativePath(cta.url)}
                                helperText={
                                  !!cta.url && !isSafeRelativePath(cta.url)
                                    ? 'Must start with "/" and contain no scheme, host or query'
                                    : ' '
                                }
                                sx={{ flex: '1 1 200px' }}
                                inputProps={{ 'data-testid': `cta-custom-path-${index}` }}
                              />
                            )}
                            <IconButton
                              aria-label="Remove CTA"
                              size="small"
                              onClick={() => removeCta(index)}
                              sx={{ mt: 0.5 }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ))}
                      </Grid>

                      {/*
                        The client app gates this whole surface (video, image and
                        the Open a tool CTA) behind its own per-stack build flag.
                        Without this notice an admin can upload a video, publish,
                        and see nothing happen for production users — which reads
                        as a broken upload rather than a flag that is off.
                      */}
                      <Grid size={{ xs: 12 }}>
                        <Alert severity="info" data-testid="notification-rich-media-notice">
                          Video, image and “Open a tool” buttons only appear for clients on stacks where
                          rich notifications are switched on. They are live on staging and in production.
                          The title, message and other button types always show.
                        </Alert>
                      </Grid>

                      <Grid size={{ xs: 12 }}>
                        <NotificationMediaUpload
                          kind="video"
                          label="Feature demo video"
                          helperText="A short clip of the feature in use — clients watch far more than they read. Record it in any screen-recorder or demo tool and upload the MP4 here. Max 100 MB."
                          value={formData.videoUrl}
                          onChange={(videoUrl) => setFormData((prev) => ({ ...prev, videoUrl }))}
                          testIdPrefix="notification-video-upload"
                        />
                      </Grid>

                      <Grid size={{ xs: 12 }}>
                        <NotificationMediaUpload
                          kind="image"
                          label="Image (optional)"
                          helperText="Shown on its own, or as the video's still frame before it plays."
                          value={formData.imageUrl}
                          onChange={(imageUrl) => setFormData((prev) => ({ ...prev, imageUrl }))}
                          testIdPrefix="notification-image-upload"
                        />
                      </Grid>

                      <Grid size={{ xs: 12 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.isModal}
                              onChange={(e) => setFormData({ ...formData, isModal: e.target.checked })}
                            />
                          }
                          label="Show as Modal (blocks UI until closed)"
                        />
                      </Grid>
                      {selectedNotification && (
                        <Grid size={{ xs: 12 }}>
                          <FormControl fullWidth>
                            <InputLabel>Status</InputLabel>
                            <Select
                              value={formData.status}
                              label="Status"
                              onChange={(e) => setFormData({ ...formData, status: e.target.value as NotificationStatus })}
                            >
                              {selectedNotification?.status === 'pending' && (
                                <MenuItem value="pending">Pending review</MenuItem>
                              )}
                              <MenuItem value="active">Active</MenuItem>
                              <MenuItem value="inactive">Inactive</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}

          {activeStep === 1 && (
            <Box sx={{ maxWidth: 1100, mx: 'auto', width: '100%', mt: 0.5 }}>
              {organizationsError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {organizationsError}
                </Alert>
              )}

              {/* Per-user targeting (Trello #246 #7) — narrows the audience to
                  specific users WITHIN the org/stack selection below. */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Target specific users (optional)
                </Typography>
                <Autocomplete
                  multiple
                  freeSolo
                  size="small"
                  options={userOptions}
                  value={formData.targetUsers}
                  loading={userSearchLoading}
                  filterOptions={(opts) => opts}
                  getOptionLabel={(o) => (typeof o === 'string' ? o : o.label)}
                  isOptionEqualToValue={(o, v) =>
                    (typeof o === 'string' ? o : o.value) === (typeof v === 'string' ? v : v.value)
                  }
                  inputValue={userSearchInput}
                  onInputChange={(_e, val, reason) => {
                    // 'reset' fires after a selection clears the box — don't treat it as a new search.
                    if (reason !== 'reset') setUserSearchInput(val);
                  }}
                  onChange={(_e, val) =>
                    setFormData({
                      ...formData,
                      targetUsers: dedupeTargetUsers(
                        (val as Array<TargetUser | string>).flatMap(normalizeTargetEntries),
                      ),
                    })
                  }
                  renderOption={(props, option) => (
                    <li {...props} key={typeof option === 'string' ? option : option.value}>
                      {typeof option === 'string' ? option : option.label}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search users by email or name"
                      placeholder="e.g. marcus@titandc.co.za"
                      helperText="Leave blank to target by organization. Search and select users, or type an email and press Enter — it resolves automatically."
                      inputProps={{ ...params.inputProps, 'data-testid': 'user-ids-input' }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {userSearchLoading ? <CircularProgress color="inherit" size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Box>

              {/* Organization targeting — available for all admin types */}
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.sendToAllOrganizations}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setFormData({
                              ...formData,
                              sendToAllOrganizations: checked,
                              organizationIds: checked ? [] : formData.organizationIds,
                            });
                          }}
                        />
                      }
                      label="Send to all organizations"
                    />

                    {!formData.sendToAllOrganizations && (
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        {formData.organizationIds.length > 0 && (
                          <Chip
                            size="small"
                            color="primary"
                            label={`${formData.organizationIds.length} selected`}
                          />
                        )}
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() =>
                            setFormData({
                              ...formData,
                              organizationIds: mergeOrgSelections(
                                formData.organizationIds,
                                filteredOrganizations.map((org) => org.id),
                              ),
                            })
                          }
                          disabled={filteredOrganizations.length === 0}
                        >
                          Select all filtered
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="inherit"
                          onClick={() => setFormData({ ...formData, organizationIds: [] })}
                          disabled={formData.organizationIds.length === 0}
                        >
                          Clear all
                        </Button>
                      </Box>
                    )}
                  </Box>

                  {formData.sendToAllOrganizations ? (
                    <Alert severity="info">
                      This notification will be visible to all current and future organizations.
                    </Alert>
                  ) : (
                    <>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Search organizations by ID, display name, or domain..."
                        value={organizationSearch}
                        onChange={(e) => setOrganizationSearch(e.target.value)}
                        sx={{ mb: 2 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                      />

                      {organizationsLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                          <CircularProgress size={28} />
                        </Box>
                      ) : (
                        <Box sx={{ height: 430 }}>
                          <DataGrid
                            rows={filteredOrganizations}
                            columns={organizationColumns}
                            checkboxSelection
                            disableRowSelectionOnClick
                            // Selections must survive the external search filter swapping
                            // `rows` out from under the grid. Without this, DataGrid prunes
                            // selected IDs that aren't in the current rows — so searching for
                            // a second org wiped the first one (Trello #215).
                            keepNonExistentRowsSelected
                            rowSelectionModel={organizationSelectionModel}
                            onRowSelectionModelChange={(newSelectionModel) => {
                              const selectedIds = (newSelectionModel as string[]).map(String);
                              setFormData({
                                ...formData,
                                sendToAllOrganizations: false,
                                organizationIds: selectedIds,
                              });
                            }}
                            pageSizeOptions={[10, 25, 50]}
                            initialState={{
                              pagination: { paginationModel: { pageSize: 10 } },
                              sorting: { sortModel: [{ field: 'name', sort: 'asc' }] },
                            }}
                            sx={{
                              border: '1px solid',
                              borderColor: 'divider',
                              '& .MuiDataGrid-cell:focus': { outline: 'none' },
                            }}
                          />
                        </Box>
                      )}
                    </>
                  )}
                </>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Box sx={{ maxWidth: 1100, mx: 'auto', width: '100%', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button onClick={handleCloseDialog} disabled={formLoading}>
              Cancel
            </Button>
            {activeStep === 1 && (
              <Button onClick={handleBackStep} disabled={formLoading}>
                Back
              </Button>
            )}
            {activeStep === 0 ? (
              <Button variant="contained" onClick={handleNextStep} disabled={formLoading}>
                Next
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleFormSubmit}
                disabled={formLoading}
                startIcon={formLoading ? <CircularProgress size={20} /> : null}
              >
                {selectedNotification ? 'Update' : 'Create'}
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      {/* Announcement email — draft, preview and send (Trello #395) */}
      <NotificationEmailDialog
        open={Boolean(emailNotification)}
        notification={emailNotification}
        organizations={organizations}
        onClose={() => setEmailNotification(null)}
        onSent={(message) => {
          showSnackbar(message, 'success');
          // Refresh so the row picks up announcementEmailSentAt and the button
          // stops looking like it has never been pressed.
          fetchNotifications();
        }}
      />

      {/* Org Detail Dialog */}
      {detailNotification && (() => {
        const targetedOrgs = organizations.filter(
          (org) => detailNotification.organizationIds?.includes(org.id)
        );
        return (
          <Dialog open={true} onClose={() => setDetailNotification(null)} maxWidth="sm" fullWidth>
            <DialogTitle>
              Targeted Organizations ({detailNotification.organizationIds?.length ?? 0})
            </DialogTitle>
            <DialogContent dividers>
              {targetedOrgs.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No matching organizations found.
                </Typography>
              ) : (
                <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                  <Box component="thead">
                    <Box component="tr">
                      <Box component="th" sx={{ textAlign: 'left', pb: 1, pr: 2, typography: 'caption', color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Organization
                      </Box>
                      <Box component="th" sx={{ textAlign: 'left', pb: 1, typography: 'caption', color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Domain
                      </Box>
                    </Box>
                  </Box>
                  <Box component="tbody">
                    {targetedOrgs.map((org, i) => (
                      <Box component="tr" key={org.id} sx={{ borderTop: i > 0 ? '1px solid' : 'none', borderColor: 'divider' }}>
                        <Box component="td" sx={{ py: 1, pr: 2 }}>
                          <Typography variant="body2" fontWeight={500}>
                            {org.displayName || org.name}
                          </Typography>
                          {org.displayName && org.displayName !== org.name && (
                            <Typography variant="caption" color="text.secondary">
                              {org.name}
                            </Typography>
                          )}
                        </Box>
                        <Box component="td" sx={{ py: 1 }}>
                          <Typography variant="body2" color="text.secondary">{org.domain || '-'}</Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailNotification(null)}>Close</Button>
            </DialogActions>
          </Dialog>
        );
      })()}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => !formLoading && setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Notification</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to permanently delete this notification?
            This action cannot be undone.
          </Typography>
          {selectedNotification && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                {selectedNotification.title || 'Untitled'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedNotification.message}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={formLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={formLoading}
            startIcon={formLoading ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NotificationsPage;
